import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";

import {
  AlertCircleIcon,
  CheckCircle2Icon,
  KeyIcon,
  Loader2Icon,
  ShieldCheckIcon,
} from "lucide-react";
import LOGO from "assets/logo.svg";
import LOGO_WHITE from "assets/logo-white.svg";
import { toast } from "utils/toast";

import { resetPassword } from "../../../api/accounts";
import { passwordRules } from "../../../utils/validators/generalValidators";

import { Button } from "js/components/ui/button";
import { Label } from "js/components/ui/label";
import { PasswordInput } from "js/components/ui/password-input";

interface PasswordResetFormData {
  new_password: string;
  confirm_new_password: string;
}

const PasswordReset = () => {
  const navigate = useNavigate();
  const { uidb64, token } = useParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<PasswordResetFormData>({
    defaultValues: {
      new_password: "",
      confirm_new_password: "",
    },
  });

  const password = watch("new_password");

  const validatePassword = (value: string) => {
    const validationErrors: string[] = [];
    passwordRules.forEach((rule) => {
      if (!rule.pattern.test(value)) {
        validationErrors.push(rule.message);
      }
    });
    return validationErrors.length === 0 || validationErrors.join(". ");
  };

  const onSubmit = async (formValues: PasswordResetFormData) => {
    if (!uidb64 || !token) {
      toast.error("Enlace de restablecimiento inválido");
      return;
    }

    setLoading(true);
    try {
      await resetPassword(formValues, uidb64, token);
      setSuccess(true);
      toast.success("Contraseña actualizada exitosamente");
    } catch (error: any) {
      console.error("Password reset failed:", error?.response?.status);
      toast.error(
        error?.response?.data?.detail || "Error al cambiar la contraseña"
      );
    } finally {
      setLoading(false);
    }
  };

  const PasswordStrengthIndicator = ({ password }: { password: string }) => {
    const checks = passwordRules.map((rule) => ({
      label: rule.message,
      passed: rule.pattern.test(password),
    }));

    if (!password) return null;

    return (
      <div className="mt-2 space-y-1.5">
        {checks.map((check, index) => (
          <div
            key={index}
            className={`flex items-center gap-2 text-xs ${
              check.passed ? "text-green-600" : "text-muted-foreground"
            }`}
          >
            {check.passed ? (
              <CheckCircle2Icon className="h-3.5 w-3.5" />
            ) : (
              <AlertCircleIcon className="h-3.5 w-3.5" />
            )}
            <span>{check.label}</span>
          </div>
        ))}
      </div>
    );
  };

  const getContent = () => {
    if (success) {
      return {
        icon: (
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2Icon className="h-10 w-10 text-green-600" />
          </div>
        ),
        title: "Contraseña actualizada",
        titleColor: "text-green-600",
        description:
          "Tu contraseña ha sido cambiada exitosamente. Ahora puedes iniciar sesión con tu nueva contraseña.",
      };
    }
    return {
      icon: (
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
          <KeyIcon className="h-10 w-10 text-primary" />
        </div>
      ),
      title: "Restablecer contraseña",
      titleColor: "text-foreground",
      description:
        "Ingresa tu nueva contraseña para recuperar el acceso a tu cuenta.",
    };
  };

  const content = getContent();

  return (
    <div className="flex min-h-screen">
      {/* Left Section - Branding */}
      <div
        className="relative hidden w-1/2 overflow-hidden lg:flex"
        style={{
          background:
            "linear-gradient(135deg, #0f0c4a 0%, #161467 50%, #1e1a7a 100%)",
        }}
      >
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient mesh */}
          <div
            className="absolute -top-40 -left-40 h-[700px] w-[700px] rounded-full opacity-50 blur-[100px]"
            style={{
              background:
                "radial-gradient(circle, rgba(124,58,237,0.6) 0%, transparent 70%)",
            }}
          />
          <div
            className="absolute -bottom-40 -right-40 h-[600px] w-[600px] rounded-full opacity-40 blur-[100px]"
            style={{
              background:
                "radial-gradient(circle, rgba(79,70,229,0.6) 0%, transparent 70%)",
            }}
          />

          {/* Top right pattern - Isometric blueprint grid */}
          <svg
            className="absolute -top-6 -right-6 h-[320px] w-[320px] opacity-[0.08]"
            viewBox="0 0 320 320"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line x1="320" y1="0" x2="160" y2="92" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="40" x2="120" y2="155" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="80" x2="80" y2="218" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="120" x2="40" y2="282" stroke="white" strokeWidth="0.5" />
            <line x1="280" y1="0" x2="0" y2="161" stroke="white" strokeWidth="0.5" />
            <line x1="240" y1="0" x2="0" y2="138" stroke="white" strokeWidth="0.5" />
            <line x1="200" y1="0" x2="0" y2="115" stroke="white" strokeWidth="0.5" />

            <line x1="320" y1="0" x2="200" y2="69" stroke="white" strokeWidth="0.5" />
            <line x1="280" y1="0" x2="120" y2="92" stroke="white" strokeWidth="0.5" />
            <line x1="240" y1="0" x2="40" y2="115" stroke="white" strokeWidth="0.5" />
            <line x1="200" y1="0" x2="0" y2="115" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="60" x2="160" y2="152" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="120" x2="200" y2="189" stroke="white" strokeWidth="0.5" />

            <path d="M260 40 L300 63 L300 103 L260 80 Z" stroke="white" strokeWidth="0.75" fill="none" />
            <path d="M260 40 L220 63 L220 103 L260 80 Z" stroke="white" strokeWidth="0.75" fill="none" />
            <path d="M220 63 L260 40 L300 63 L260 86 Z" stroke="white" strokeWidth="0.75" fill="none" />

            <path d="M180 90 L210 106 L210 136 L180 120 Z" stroke="white" strokeWidth="0.5" fill="none" />
            <path d="M180 90 L150 106 L150 136 L180 120 Z" stroke="white" strokeWidth="0.5" fill="none" />
            <path d="M150 106 L180 90 L210 106 L180 122 Z" stroke="white" strokeWidth="0.5" fill="none" />

            <rect x="240" y="140" width="50" height="35" stroke="white" strokeWidth="0.5" fill="none" transform="skewY(-30) skewX(0)" />
            <line x1="265" y1="110" x2="265" y2="135" stroke="white" strokeWidth="0.5" strokeDasharray="2 2" />

            <circle cx="300" cy="63" r="2" fill="white" fillOpacity="0.4" />
            <circle cx="260" cy="40" r="2" fill="white" fillOpacity="0.4" />
            <circle cx="220" cy="63" r="2" fill="white" fillOpacity="0.3" />
          </svg>

          {/* Bottom left pattern - Isometric blueprint grid */}
          <svg
            className="absolute -bottom-6 -left-6 h-[280px] w-[280px] opacity-[0.08]"
            viewBox="0 0 280 280"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line x1="0" y1="280" x2="140" y2="199" stroke="white" strokeWidth="0.5" />
            <line x1="0" y1="240" x2="160" y2="148" stroke="white" strokeWidth="0.5" />
            <line x1="0" y1="200" x2="180" y2="96" stroke="white" strokeWidth="0.5" />
            <line x1="40" y1="280" x2="200" y2="188" stroke="white" strokeWidth="0.5" />
            <line x1="80" y1="280" x2="240" y2="188" stroke="white" strokeWidth="0.5" />

            <line x1="0" y1="280" x2="100" y2="222" stroke="white" strokeWidth="0.5" />
            <line x1="0" y1="240" x2="80" y2="194" stroke="white" strokeWidth="0.5" />
            <line x1="0" y1="200" x2="60" y2="165" stroke="white" strokeWidth="0.5" />

            <path d="M40 240 L80 218 L120 240 L120 270 L80 292 L40 270 Z" stroke="white" strokeWidth="0.75" fill="none" />
            <line x1="80" y1="218" x2="80" y2="248" stroke="white" strokeWidth="0.5" />

            <line x1="60" y1="229" x2="60" y2="255" stroke="white" strokeWidth="0.5" strokeDasharray="3 2" />
            <line x1="100" y1="229" x2="100" y2="255" stroke="white" strokeWidth="0.5" strokeDasharray="3 2" />

            <path d="M150 220 L175 206 L175 236 L150 250 Z" stroke="white" strokeWidth="0.5" fill="none" />
            <path d="M150 220 L125 206 L125 236 L150 250 Z" stroke="white" strokeWidth="0.5" fill="none" />

            <circle cx="80" cy="218" r="2" fill="white" fillOpacity="0.4" />
            <circle cx="40" cy="240" r="1.5" fill="white" fillOpacity="0.3" />
            <circle cx="120" cy="240" r="1.5" fill="white" fillOpacity="0.3" />
          </svg>

          {/* Subtle noise texture */}
          <div
            className="absolute inset-0 opacity-[0.25]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              mixBlendMode: "overlay",
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 flex w-full flex-col items-center justify-between p-8 text-white xl:p-12">
          {/* Logo */}
          <img
            src={LOGO_WHITE}
            alt="Multiburo"
            className="absolute left-8 top-8 h-14 w-auto xl:left-12 xl:top-12 xl:h-16"
          />

          {/* Center Content */}
          <div className="flex flex-1 flex-col items-center justify-center">
            <motion.div
              className="max-w-md text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Security Icon */}
              <motion.div
                className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full"
                style={{ background: "rgba(255, 255, 255, 0.1)" }}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.2 }}
              >
                <ShieldCheckIcon className="h-10 w-10 text-white/90" />
              </motion.div>

              <h2 className="mb-3 text-3xl font-bold leading-tight tracking-tight xl:text-4xl">
                Protege tu cuenta
              </h2>
              <p className="text-lg text-white/70">
                Crea una contraseña segura para mantener tu información
                protegida. Te recomendamos usar una combinación de letras,
                números y símbolos.
              </p>
            </motion.div>
          </div>

          {/* Footer */}
          <div className="text-center text-sm text-white/40">
            {new Date().getFullYear()} Todos los derechos reservados
          </div>
        </div>
      </div>

      {/* Right Section - Form */}
      <div className="relative flex flex-1 flex-col justify-center px-6 py-12 sm:px-12 lg:px-16">
        <div className="mx-auto w-full max-w-md space-y-8">
          {/* Logo - visible only on mobile */}
          <motion.div
            className="flex justify-start lg:hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <img src={LOGO} alt="Multiburo" className="h-14 w-auto" />
          </motion.div>

          {/* Icon */}
          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            {content.icon}
          </motion.div>

          {/* Header */}
          <motion.div
            className="space-y-2 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <h1
              className={`text-2xl font-bold tracking-tight sm:text-3xl ${content.titleColor}`}
            >
              {content.title}
            </h1>
            <p className="text-muted-foreground">{content.description}</p>
          </motion.div>

          {/* Form or Success Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            {success ? (
              <div className="space-y-4">
                <Button
                  onClick={() => navigate("/login")}
                  className="h-11 w-full text-base font-semibold"
                >
                  Ir al inicio de sesión
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="new_password">Nueva contraseña</Label>
                  <Controller
                    name="new_password"
                    control={control}
                    rules={{
                      required: "La contraseña es requerida",
                      validate: validatePassword,
                    }}
                    render={({ field }) => (
                      <PasswordInput
                        id="new_password"
                        placeholder="********"
                        className={`h-11 ${errors.new_password ? "border-destructive" : ""}`}
                        {...field}
                      />
                    )}
                  />
                  <PasswordStrengthIndicator password={password || ""} />
                  {errors.new_password && (
                    <p className="text-sm text-destructive">
                      {errors.new_password.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_new_password">
                    Confirmar nueva contraseña
                  </Label>
                  <Controller
                    name="confirm_new_password"
                    control={control}
                    rules={{
                      required: "Confirma tu contraseña",
                      validate: (value) =>
                        value === password || "Las contraseñas no coinciden",
                    }}
                    render={({ field }) => (
                      <PasswordInput
                        id="confirm_new_password"
                        placeholder="********"
                        className={`h-11 ${errors.confirm_new_password ? "border-destructive" : ""}`}
                        {...field}
                      />
                    )}
                  />
                  {errors.confirm_new_password && (
                    <p className="text-sm text-destructive">
                      {errors.confirm_new_password.message}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="h-11 w-full text-base font-semibold"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                      Actualizando...
                    </>
                  ) : (
                    "Restablecer contraseña"
                  )}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 font-semibold"
                    onClick={() => navigate("/login")}
                  >
                    Volver al inicio de sesión
                  </Button>
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
